import React, { FC, useEffect, useState } from "react";
import {
  DashboardTopBar,
  ItemContentArea,
  TableGrid,
} from "../../../shared/components";
import { observer } from "mobx-react";
import { Params } from "react-router-dom";
import { firstValueFrom } from "rxjs";
import {
  CollapsiblePanel,
  CustomButton,
  CustomLabel,
  FlexContainer,
  ITableHeaders,
  Loader,
  TableWithHeaderButtons,
  UtilityMethods,
  withRouter,
} from "@mindmemobile/mm-shared-global";
import {
  AccountSettingsStore,
  ItemsStore,
  RolesAndPermissionStore,
} from "../../../shared/store";
import { AuthService } from "../../../shared/services";
import {
  DescriptionCell,
  ItemDetailLeftPanel,
  ItemDetailMainWrapper,
  ItemDetailRightPanel,
  LabelCell,
  MainOuterCard,
} from "../../../shared/styles";
import { MerchantAccountStore } from "../../merchantAccounts";
import FundingService from "../../../shared/services/FundingService";
import FundingStore from "../../../shared/store/FundingStore";
import { FundingUtil } from "../../../shared/utils/FundingUtil";
import styled from "styled-components";
import MindMeLogo from "../../../../../../images/common/mindMe-solo-logo.svg";
import {
  PayoutPaymentGridModel,
  SelectedItemModel,
} from "../../../shared/models";
import { GridNames } from "../../../shared/enums";
import PaymentIcon from "../../../../../../images/membership/dollar-blue.svg";

type IProps = {
  payoutId: string;
  params: Readonly<Params<string>>;
  fundingStore: FundingStore;
  itemStore: ItemsStore;
  rolesAndPermissionStore: RolesAndPermissionStore;
  merchantAccountStore: MerchantAccountStore;
};

const FundingDetailPage: FC<IProps> = ({
  params,
  fundingStore,
  itemStore,
  rolesAndPermissionStore,
  merchantAccountStore,
}) => {
  const [showLoader, setShowLoader] = useState(true);

  function payoutId() {
    const id = params.id || "";
    return id;
  }

  function merchantAccountId() {
    const mId = params.mId || "";
    return mId;
  }

  useEffect(() => {
    fetchData();
    itemStore?.setBreadCrumbData(
      new SelectedItemModel({
        route: "/app/funding",
        title: `Payout ${fundingStore.detailPage.detail.payoutNumber.toString()}`,
      })
    );

    return () => {
      itemStore?.setBreadCrumbData();
    };
  }, [payoutId()]);

  const fetchData = async () => {
    setShowLoader(true);
    const response = await firstValueFrom(
      FundingService.getPayoutDetails(
        AuthService.authData.accountId,
        merchantAccountId(),
        payoutId()
      )
    );
    fundingStore.deserializeDetailPageAPIResponse(response.result);
    setShowLoader(false);
  };

  if (fundingStore.detailPage === null) return null;

  function renderDetails(node: React.ReactNode) {
    if (!fundingStore.detailPage) return null;
    return (
      <>
        <DashboardTopBar
          itemStore={itemStore}
          rightContent={<></>}
          rolesAndPermissionStore={rolesAndPermissionStore}
          merchantAccountStore={merchantAccountStore}
        />
        <ItemContentArea
          node={node}
          itemStore={itemStore}
          showLoader={showLoader}
          showMerchantAccountNotificationBar={merchantAccountStore.hasNotificationBar()}
        />
      </>
    );
  }
  const row = (
    label: string,
    rightColumn: string | React.ReactNode,
    className?: string
  ): React.ReactNode => {
    return (
      <FlexContainer className={`label-description-row ${className}`}>
        <LabelCell>
          <CustomLabel className={className}>{label}</CustomLabel>
        </LabelCell>
        <DescriptionCell>{rightColumn}</DescriptionCell>
      </FlexContainer>
    );
  };

  const detailSectionContent = () => {
    const detail = fundingStore.detailPage.detail;
    const colors = FundingUtil.getColorsByStatus(detail.status);
    const feeTypeRow = (index: number, amount: number, label: string) => {
      return (
        <div key={index}>
          <FlexContainer alignItems="center" spacing={50}>
            <CustomLabel fontSize={"14px"} lineHeight="20px">
              {AccountSettingsStore.formatAmountText(amount, true)}
            </CustomLabel>
            <CustomLabel fontSize={"14px"}>{label}</CustomLabel>
          </FlexContainer>
        </div>
      );
    };
    return (
      <FlexContainer>
        <ItemDetailLeftPanel>
          {row(
            "Status",
            <CustomButton
              minWidth={"90px"}
              padding="6px 10px"
              borderRadius="4px !important"
              color={colors.color}
              bgColor={colors.bgColor}
              className="status-btn"
            >
              {detail.status}
            </CustomButton>,
            "status-lh"
          )}
          {row(
            "Date",
            <CustomLabel>
              {(detail.createdDate &&
                UtilityMethods.getDayCommaMonthDateCommaYear(
                  detail.createdDate,
                  false
                )) ||
                "-"}
            </CustomLabel>
          )}
          {row(
            "Total",
            <CustomLabel fontWeight={"600"}>
              {AccountSettingsStore.formatAmountText(detail.totalAmount, true)}{" "}
              <span className="usd-grey">USD</span>
            </CustomLabel>
          )}
          <FlexContainer>
            <CollapsiblePanel title="Fees" className="fee-funding-section">
              <FlexContainer fxLayout="column" spacing={5}>
                <CustomLabel fontSize={"16px"} margin="0px 0px 10px">
                  {AccountSettingsStore.formatAmountText(
                    detail.totalFees,
                    true
                  )}
                </CustomLabel>
                {/* Krishma uncomment this */}
                {/* {feeTypeRow(0, detail.processingFees, "Processing Fee")}
                {feeTypeRow(1, detail.additionalFees, "Additional Fee")}
                {feeTypeRow(2, detail.batchFees, "Batch Fee")}
                {feeTypeRow(3, detail.rejectFees, "Reject Fee")} */}
              </FlexContainer>
            </CollapsiblePanel>
          </FlexContainer>
          {row(
            "Payout",
            <CustomLabel fontWeight={"600"}>
              {AccountSettingsStore.formatAmountText(detail.payoutAmount, true)}
            </CustomLabel>
          )}
        </ItemDetailLeftPanel>
        <ItemDetailRightPanel>
          {row("Payout No", <CustomLabel>{detail.payoutNumber}</CustomLabel>)}
          {row("Payout ID", <CustomLabel>{detail.payoutId}</CustomLabel>)}
          {row(
            "Merchant Account",
            <FlexContainer fxLayout="column" spacing={5}>
              <CustomLabel> {detail.merchantAccount?.name}</CustomLabel>
              {/* <CustomLabel>School Operating</CustomLabel>
              <CustomLabel>TD Bank ...583011</CustomLabel> */}
            </FlexContainer>
          )}
          {row(
            "Gateway",
            <>
              {/* <CustomLabel>{detail.merchantAccount.gateway}</CustomLabel> */}
              <img src={MindMeLogo} />
            </>,
            "gateway-col"
          )}
        </ItemDetailRightPanel>
      </FlexContainer>
    );
  };
  function columns() {
    const columns: ITableHeaders<PayoutPaymentGridModel>[] = [
      {
        dataKey: "status",
        title: "Status",
        onCellRender: (data: PayoutPaymentGridModel) => {
          const colors = data.getColorsByStatus();
          return (
            <CustomButton
              padding="6px 10px"
              borderRadius="4px !important"
              color={colors.color}
              bgColor={colors.bgColor}
              className="status-btn"
              width="90px"
            >
              {data.status}
            </CustomButton>
          );
        },
      },
      {
        dataKey: "createdDate",
        title: "Date",
        onCellRender: (data: PayoutPaymentGridModel) => {
          return (
            <CustomLabel fontSize={"14px"} lineHeight="20px">
              {UtilityMethods.getDayCommaMonthDateCommaYear(
                data.createdDate,
                false
              )}
            </CustomLabel>
          );
        },
      },
      {
        dataKey: "amount",
        title: "Amount",
        onCellRender: (data: PayoutPaymentGridModel) => {
          return (
            <CustomLabel fontSize={"14px"} lineHeight="20px">
              {AccountSettingsStore.formatAmountText(data.amount, true)}
            </CustomLabel>
          );
        },
      },
    ];
    return columns;
  }
  const renderPaymentGridContent = () => {
    const totalItemCount = fundingStore.detailPage.payments.length;
    const isNoData = totalItemCount === 0;
    if (isNoData) return null;
    return (
      <TableGrid
        tableHeader={
          <TableWithHeaderButtons
            totalItems={totalItemCount}
            icon={PaymentIcon}
            title="Payments"
          />
        }
        totalItemCount={totalItemCount}
        cellSpacing={20}
        data={fundingStore.detailPage.payments}
        headers={columns()}
        name={GridNames.Payment}
        hideTable={isNoData}
        wrapperClassName="table-with-header-btns table-header-max-height"
      />
    );
  };
  if (showLoader) return <>{new Loader(true).loaderComponent}</>;
  return renderDetails(
    <ItemDetailMainWrapper className="funding-detail-page">
      <MainOuterCard>
        <FlexContainer
          alignItems="center"
          padding="0px 0px 40px"
          justifyContent="space-between"
          className="position-relative"
        >
          <CustomLabel
            color="#2C3E50"
            fontSize="30px"
            fontWeight="600"
            lineHeight="40px"
          >
            Funding
          </CustomLabel>
        </FlexContainer>
        <CustomLabel
          color="#2C3E50"
          fontSize={"20px"}
          fontWeight={"600"}
          margin="5px 0px 25px"
        >
          Payout
        </CustomLabel>
        {detailSectionContent()}
      </MainOuterCard>
      {renderPaymentGridContent()}
    </ItemDetailMainWrapper>
  );
};
export default withRouter(observer(FundingDetailPage));
